"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { useDispatch } from "react-redux"
import { addOrder } from "../../store/slices/ordersSlice"
import { clearCart } from "../../store/slices/cartSlice"
import { phonePeService } from "../../services/phonepeService"
import { razorpayService } from "../../services/razorpayService"
import { ordersService } from "../../services/firebase"
import { paymentSuccess, paymentFailed } from "../../store/slices/phonepeSlice"
import { CheckCircle, XCircle, Loader2 } from "lucide-react"
import toast from "react-hot-toast"

const PaymentCallback: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<"loading" | "success" | "failed">("loading")
  const [message, setMessage] = useState("")

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get transaction ID from URL parameters
        const merchantTransactionId = searchParams.get("merchantTransactionId")
        const razorpayPaymentId = searchParams.get("razorpay_payment_id")
        const razorpayOrderId = searchParams.get("razorpay_order_id")
        const razorpaySignature = searchParams.get("razorpay_signature")

        // Razorpay redirect fallback: finalize order if present
        if (razorpayPaymentId && razorpayOrderId && razorpaySignature) {
          const verify = await razorpayService.verifyPayment({
            orderId: razorpayOrderId,
            paymentId: razorpayPaymentId,
            signature: razorpaySignature,
          })

          if (verify.success) {
            const alreadyFinalized = localStorage.getItem("pendingOrderFinalized") === "true"
            const pendingOrderRaw = localStorage.getItem("pendingOrder")
            if (pendingOrderRaw && !alreadyFinalized) {
              const pendingOrder = JSON.parse(pendingOrderRaw)
              const finalOrder = {
                ...pendingOrder,
                paymentId: razorpayPaymentId,
                merchantTransactionId: razorpayOrderId,
                paymentStatus: "completed",
                paymentMethod: pendingOrder.paymentMethod || "online",
              }
              await ordersService.addOrder(finalOrder)
              dispatch(addOrder(finalOrder))
              dispatch(clearCart())
              localStorage.removeItem("pendingOrder")
              localStorage.setItem("pendingOrderFinalized", "true")
            }

            setStatus("success")
            setMessage("Payment completed successfully!")
            toast.success("Payment successful!")
            setTimeout(() => navigate("/orders"), 3000)
            return
          }
        }

        if (!merchantTransactionId) {
          setStatus("failed")
          setMessage("Invalid payment callback")
          return
        }

        // Check payment status
        const statusResult = await phonePeService.checkPaymentStatus(merchantTransactionId)

        if (statusResult.success && statusResult.data) {
          const { state, transactionId: txnId, responseCode } = statusResult.data

          if (state === "COMPLETED") {
            setStatus("success")
            setMessage("Payment completed successfully!")
            dispatch(paymentSuccess({ transactionId: txnId, merchantTransactionId }))
            toast.success("Payment successful!")

            // Redirect to orders page after 3 seconds
            setTimeout(() => {
              navigate("/orders")
            }, 3000)
          } else if (state === "FAILED") {
            setStatus("failed")
            setMessage(`Payment failed: ${responseCode}`)
            dispatch(paymentFailed({ error: `Payment failed: ${responseCode}` }))
            toast.error("Payment failed")

            // Redirect to checkout after 3 seconds
            setTimeout(() => {
              navigate("/checkout")
            }, 3000)
          } else {
            setStatus("loading")
            setMessage("Payment is being processed...")

            // Continue polling for pending payments
            setTimeout(() => {
              verifyPayment()
            }, 2000)
          }
        } else {
          setStatus("failed")
          setMessage("Unable to verify payment status")
          dispatch(paymentFailed({ error: "Payment verification failed" }))
        }
      } catch (error) {
        console.error("Payment verification error:", error)
        setStatus("failed")
        setMessage("Payment verification failed")
        dispatch(paymentFailed({ error: "Payment verification failed" }))
      }
    }

    verifyPayment()
  }, [searchParams, dispatch, navigate])

  const renderContent = () => {
    switch (status) {
      case "loading":
        return (
          <div className="text-center">
            <Loader2 className="w-16 h-16 text-primary-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Payment</h2>
            <p className="text-gray-600">{message}</p>
          </div>
        )

      case "success":
        return (
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500">Redirecting to your orders...</p>
          </div>
        )

      case "failed":
        return (
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/checkout")}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate("/cart")}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Back to Cart
              </button>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8">{renderContent()}</div>
    </div>
  )
}

export default PaymentCallback
